import React, { useState, useRef, useEffect, useCallback } from "react";
import "../../components/skeletonRow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faSync,
  faCopy,
  faCheck,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler);

// ─── Helpers: Date Formatting to DD-MM-YYYY ─────────────────────────────────

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "-";
  const str = String(dateStr).trim();
  if (str === "" || str.toUpperCase() === "NULL") return "-";

  // If already in DD-MM-YYYY HH:mm:ss format
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(str)) {
    return str.split(" ")[0];
  }
  // If in YYYY-MM-DD HH:mm:ss format
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) {
    const [datePart] = str.split(" ");
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }
  // If in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split("-");
    return `${day}-${month}-${year}`;
  }
  // If already in DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    return str;
  }

  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatDisplayDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const str = String(dateStr).trim();
  if (str === "" || str.toUpperCase() === "NULL") return "-";

  // If already in DD-MM-YYYY HH:mm:ss format
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(str)) return str;

  // If in YYYY-MM-DD HH:mm:ss format
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) {
    const [datePart, timePart] = str.split(" ");
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year} ${timePart}`;
  }

  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
};

// ─── Default States ─────────────────────────────────────────────────────────

const defaultGroupedInputs = {
  stype: "all",
  utm_source: "",
  utm_campaign: "",
  utm_medium: "",
  from_created_on: "",
  to_created_on: ""
};

const defaultGroupedFilters = {
  ...defaultGroupedInputs,
  page: 1,
  limit: 100
};

const defaultDetailInputs = {
  pagename: "",
  device_type: "",
  ip_address: "",
  referer: "",
  request_url: "",
  user_agent: "",
  from_created_on: "",
  to_created_on: ""
};

const defaultDetailFilters = {
  ...defaultDetailInputs,
  orderbycol: "created_on",
  orderby: "desc",
  page: 1,
  limit: 100
};

const LinkTrackingReport = () => {
  // View mode: "grouped" | "detail"
  const [view, setView] = useState("grouped");
  const [selectedUtm, setSelectedUtm] = useState({ source: "", campaign: "", medium: "" });

  // ─── Grouped View State ───────────────────────────────────────────────────
  const [groupedLoading, setGroupedLoading] = useState(false);
  const [groupedData, setGroupedData] = useState([]);
  const [groupedTotal, setGroupedTotal] = useState(0);
  const [groupedFormInputs, setGroupedFormInputs] = useState(() => {
    try {
      const saved = sessionStorage.getItem("lt_grouped_inputs");
      return saved ? { ...defaultGroupedInputs, ...JSON.parse(saved) } : defaultGroupedInputs;
    } catch {
      return defaultGroupedInputs;
    }
  });
  const [groupedFilters, setGroupedFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem("lt_grouped_filters");
      return saved ? { ...defaultGroupedFilters, ...JSON.parse(saved) } : defaultGroupedFilters;
    } catch {
      return defaultGroupedFilters;
    }
  });

  const [overviewMetrics, setOverviewMetrics] = useState({
    total: 0,
    desktop: 0,
    android: 0,
    ios: 0,
    uniqueIps: 0,
    utmClicks: 0
  });
  const [overviewCharts, setOverviewCharts] = useState({
    devices: [],
    channels: [],
    dailyTrend: [],
    campaigns: [],
    utmSources: [],
    utmMediums: []
  });
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    streetgains: 0,
    folio: 0
  });

  // ─── Detail View State ────────────────────────────────────────────────────
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailMetrics, setDetailMetrics] = useState({
    total: 0,
    desktop: 0,
    android: 0,
    ios: 0,
    uniqueIps: 0,
    utmClicks: 0
  });
  const [detailCharts, setDetailCharts] = useState({
    devices: [],
    channels: [],
    dailyTrend: []
  });
  const [detailFormInputs, setDetailFormInputs] = useState(defaultDetailInputs);
  const [detailFilters, setDetailFilters] = useState(defaultDetailFilters);

  // Common UI State
  const [exportLoading, setExportLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // ─── Double Scroll Refs ───────────────────────────────────────────────────
  const groupedTableContainerRef = useRef(null);
  const groupedTopScrollbarRef = useRef(null);
  const groupedTopScrollbarContentRef = useRef(null);

  const detailTableContainerRef = useRef(null);
  const detailTopScrollbarRef = useRef(null);
  const detailTopScrollbarContentRef = useRef(null);

  // Persist Grouped Filters
  useEffect(() => {
    sessionStorage.setItem("lt_grouped_filters", JSON.stringify(groupedFilters));
  }, [groupedFilters]);

  useEffect(() => {
    sessionStorage.setItem("lt_grouped_inputs", JSON.stringify(groupedFormInputs));
  }, [groupedFormInputs]);

  // Sync double scroll for Grouped table
  useEffect(() => {
    if (view !== "grouped") return;
    const updateScrollbar = () => {
      if (groupedTableContainerRef.current && groupedTopScrollbarContentRef.current) {
        const tableWidth = groupedTableContainerRef.current.scrollWidth;
        groupedTopScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [groupedData, view]);

  // Sync double scroll for Detail table
  useEffect(() => {
    if (view !== "detail") return;
    const updateScrollbar = () => {
      if (detailTableContainerRef.current && detailTopScrollbarContentRef.current) {
        const tableWidth = detailTableContainerRef.current.scrollWidth;
        detailTopScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [detailData, view]);

  const isSyncingGroupedScrollRef = useRef(false);
  const lastGroupedTableScrollLeftRef = useRef(0);
  const isSyncingDetailScrollRef = useRef(false);
  const lastDetailTableScrollLeftRef = useRef(0);

  const syncGroupedScroll = () => {
    if (groupedTableContainerRef.current && groupedTopScrollbarRef.current) {
      const currentLeft = groupedTableContainerRef.current.scrollLeft;
      if (currentLeft === lastGroupedTableScrollLeftRef.current) return;
      lastGroupedTableScrollLeftRef.current = currentLeft;
      if (isSyncingGroupedScrollRef.current) return;
      isSyncingGroupedScrollRef.current = true;
      groupedTopScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingGroupedScrollRef.current = false;
      });
    }
  };

  const handleGroupedTopScroll = () => {
    if (groupedTableContainerRef.current && groupedTopScrollbarRef.current) {
      const currentLeft = groupedTopScrollbarRef.current.scrollLeft;
      if (isSyncingGroupedScrollRef.current) return;
      isSyncingGroupedScrollRef.current = true;
      groupedTableContainerRef.current.scrollLeft = currentLeft;
      lastGroupedTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingGroupedScrollRef.current = false;
      });
    }
  };

  const syncDetailScroll = () => {
    if (detailTableContainerRef.current && detailTopScrollbarRef.current) {
      const currentLeft = detailTableContainerRef.current.scrollLeft;
      if (currentLeft === lastDetailTableScrollLeftRef.current) return;
      lastDetailTableScrollLeftRef.current = currentLeft;
      if (isSyncingDetailScrollRef.current) return;
      isSyncingDetailScrollRef.current = true;
      detailTopScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingDetailScrollRef.current = false;
      });
    }
  };

  const handleDetailTopScroll = () => {
    if (detailTableContainerRef.current && detailTopScrollbarRef.current) {
      const currentLeft = detailTopScrollbarRef.current.scrollLeft;
      if (isSyncingDetailScrollRef.current) return;
      isSyncingDetailScrollRef.current = true;
      detailTableContainerRef.current.scrollLeft = currentLeft;
      lastDetailTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingDetailScrollRef.current = false;
      });
    }
  };

  // ─── Fetch Grouped Data ───────────────────────────────────────────────────
  const fetchGroupedReport = useCallback(async () => {
    setGroupedLoading(true);
    try {
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL || ""}/`;
      const params = {
        stype: groupedFilters.stype !== "all" ? groupedFilters.stype : undefined,
        utm_source: groupedFilters.utm_source || undefined,
        utm_campaign: groupedFilters.utm_campaign || undefined,
        utm_medium: groupedFilters.utm_medium || undefined,
        from_created_on: groupedFilters.from_created_on || undefined,
        to_created_on: groupedFilters.to_created_on || undefined
      };

      const [groupedRes, overviewRes] = await Promise.all([
        axios.get(`${baseUrl}reports/link-tracking/utm-grouped`, { params }),
        axios.get(`${baseUrl}reports/link-tracking`, {
          params: { ...params, page: 1, limit: 1, timeline_group: "daily" }
        })
      ]);

      if (groupedRes.data && groupedRes.data.status) {
        setGroupedData(groupedRes.data.data || []);
        setGroupedTotal(groupedRes.data.total || (groupedRes.data.data ? groupedRes.data.data.length : 0));
      }

      if (overviewRes.data && overviewRes.data.status) {
        if (overviewRes.data.metrics) setOverviewMetrics(overviewRes.data.metrics);
        if (overviewRes.data.charts) setOverviewCharts(overviewRes.data.charts);
        if (overviewRes.data.tabCounts) setTabCounts(overviewRes.data.tabCounts);
      }
    } catch (error) {
      console.error("Failed to fetch UTM grouped report:", error);
    } finally {
      setGroupedLoading(false);
    }
  }, [groupedFilters]);

  useEffect(() => {
    if (view === "grouped") {
      fetchGroupedReport();
    }
  }, [fetchGroupedReport, view]);

  // ─── Fetch Detail Data ────────────────────────────────────────────────────
  const fetchDetailReport = useCallback(async () => {
    setDetailLoading(true);
    try {
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL || ""}/`;
      const params = {
        page: detailFilters.page,
        limit: detailFilters.limit,
        stype: groupedFilters.stype !== "all" ? groupedFilters.stype : undefined,
        utm_source: selectedUtm.source !== "(none)" ? selectedUtm.source : "",
        utm_campaign: selectedUtm.campaign !== "(none)" ? selectedUtm.campaign : "",
        utm_medium: selectedUtm.medium !== "(none)" ? selectedUtm.medium : "",
        pagename: detailFilters.pagename || undefined,
        device_type: detailFilters.device_type || undefined,
        ip_address: detailFilters.ip_address || undefined,
        referer: detailFilters.referer || undefined,
        request_url: detailFilters.request_url || undefined,
        user_agent: detailFilters.user_agent || undefined,
        from_created_on: detailFilters.from_created_on || groupedFilters.from_created_on || undefined,
        to_created_on: detailFilters.to_created_on || groupedFilters.to_created_on || undefined,
        orderbycol: detailFilters.orderbycol,
        orderby: detailFilters.orderby,
        timeline_group: "daily"
      };

      const res = await axios.get(`${baseUrl}reports/link-tracking/utm-detail`, { params });

      if (res.data && res.data.status) {
        setDetailData(res.data.data || []);
        setDetailTotal(res.data.total || 0);
        if (res.data.metrics) setDetailMetrics(res.data.metrics);
        if (res.data.charts) setDetailCharts(res.data.charts);
      }
    } catch (error) {
      console.error("Failed to fetch UTM detail report:", error);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedUtm, detailFilters, groupedFilters.stype, groupedFilters.from_created_on, groupedFilters.to_created_on]);

  useEffect(() => {
    if (view === "detail") {
      fetchDetailReport();
    }
  }, [fetchDetailReport, view]);

  // ─── Filter & Navigation Handlers ─────────────────────────────────────────

  const handleGroupedInputChange = (e) => {
    const { name, value } = e.target;
    setGroupedFormInputs(prev => ({ ...prev, [name]: value }));
  };

  const applyGroupedFilters = () => {
    setGroupedFilters(prev => ({
      ...prev,
      ...groupedFormInputs,
      page: 1
    }));
  };

  const handleGroupedKeyDown = (e) => {
    if (e.key === "Enter") {
      applyGroupedFilters();
    }
  };

  const clearGroupedFilters = () => {
    sessionStorage.removeItem("lt_grouped_filters");
    sessionStorage.removeItem("lt_grouped_inputs");
    setGroupedFormInputs(defaultGroupedInputs);
    setGroupedFilters(defaultGroupedFilters);
  };

  const hasAnyGroupedFilter = () => {
    return (
      (groupedFilters.stype && groupedFilters.stype !== "all") ||
      groupedFilters.utm_source ||
      groupedFilters.utm_campaign ||
      groupedFilters.utm_medium ||
      groupedFilters.from_created_on ||
      groupedFilters.to_created_on
    );
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    setDetailFormInputs(prev => ({ ...prev, [name]: value }));
  };

  const applyDetailFilters = () => {
    setDetailFilters(prev => ({
      ...prev,
      ...detailFormInputs,
      page: 1
    }));
  };

  const handleDetailKeyDown = (e) => {
    if (e.key === "Enter") {
      applyDetailFilters();
    }
  };

  const handleDetailSort = (column) => {
    setDetailFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "desc" ? "asc" : "desc",
      page: 1
    }));
  };

  const getDetailSortIcon = (column) => {
    if (detailFilters.orderbycol !== column) return faSort;
    return detailFilters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const clearDetailFilters = () => {
    setDetailFormInputs(defaultDetailInputs);
    setDetailFilters(defaultDetailFilters);
  };

  const hasAnyDetailFilter = () => {
    return (
      detailFilters.pagename ||
      detailFilters.device_type ||
      detailFilters.ip_address ||
      detailFilters.referer ||
      detailFilters.request_url ||
      detailFilters.user_agent ||
      detailFilters.from_created_on ||
      detailFilters.to_created_on ||
      detailFilters.orderbycol !== "created_on" ||
      detailFilters.orderby !== "desc"
    );
  };

  const handleDetailPageChange = (newPage) => {
    const totalPages = Math.ceil(detailTotal / detailFilters.limit) || 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setDetailFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handlePlatformChange = (stype) => {
    setGroupedFormInputs(prev => ({ ...prev, stype }));
    setGroupedFilters(prev => ({ ...prev, stype, page: 1 }));
  };

  // ─── Open Detail ──────────────────────────────────────────────────────────
  const openDetail = (row) => {
    setSelectedUtm({
      source: row.utm_source || "",
      campaign: row.utm_campaign || "",
      medium: row.utm_medium || ""
    });
    setDetailFormInputs(defaultDetailInputs);
    setDetailFilters(defaultDetailFilters);
    setView("detail");
    window.scrollTo(0, 0);
  };

  // ─── Copy & Export ────────────────────────────────────────────────────────
  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL || ""}/`;
      const exportParams = view === "detail" ? {
        stype: groupedFilters.stype !== "all" ? groupedFilters.stype : undefined,
        utm_source: selectedUtm.source !== "(none)" ? selectedUtm.source : undefined,
        utm_campaign: selectedUtm.campaign !== "(none)" ? selectedUtm.campaign : undefined,
        utm_medium: selectedUtm.medium !== "(none)" ? selectedUtm.medium : undefined,
        pagename: detailFilters.pagename || undefined,
        device_type: detailFilters.device_type || undefined,
        ip_address: detailFilters.ip_address || undefined,
        referer: detailFilters.referer || undefined,
        request_url: detailFilters.request_url || undefined,
        user_agent: detailFilters.user_agent || undefined,
        from_created_on: detailFilters.from_created_on || groupedFilters.from_created_on || undefined,
        to_created_on: detailFilters.to_created_on || groupedFilters.to_created_on || undefined,
        orderbycol: detailFilters.orderbycol,
        orderby: detailFilters.orderby
      } : {
        stype: groupedFilters.stype !== "all" ? groupedFilters.stype : undefined,
        utm_source: groupedFilters.utm_source || undefined,
        utm_campaign: groupedFilters.utm_campaign || undefined,
        utm_medium: groupedFilters.utm_medium || undefined,
        from_created_on: groupedFilters.from_created_on || undefined,
        to_created_on: groupedFilters.to_created_on || undefined
      };

      const response = await axios.get(`${baseUrl}reports/link-tracking/export`, {
        params: exportParams,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Link_Tracking_${view === 'detail' ? 'Detail_' : ''}Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export report:", error);
    } finally {
      setExportLoading(false);
    }
  };

  // ─── Pagination helpers ───────────────────────────────────────────────────
  const detailTotalPages = Math.ceil(detailTotal / detailFilters.limit) || 1;
  const detailStartRecord = detailTotal === 0 ? 0 : ((detailFilters.page - 1) * detailFilters.limit) + 1;
  const detailEndRecord = Math.min(detailFilters.page * detailFilters.limit, detailTotal);
  const detailShowingRecords = `${detailStartRecord} to ${detailEndRecord} of ${detailTotal}`;

  // ─── Chart Data Configurations ────────────────────────────────────────────
  const activeMetrics = view === "detail" ? detailMetrics : overviewMetrics;
  const activeCharts = view === "detail" ? detailCharts : overviewCharts;

  // 1. Click Activity Timeline (LINE Chart with DD-MM-YYYY dates)
  const timelineChartData = {
    labels: (activeCharts.dailyTrend || []).map(d => formatDisplayDate(d.date)),
    datasets: [{
      label: 'Clicks',
      data: (activeCharts.dailyTrend || []).map(d => d.count),
      borderColor: '#348fe2',
      backgroundColor: 'rgba(52, 143, 226, 0.12)',
      fill: true,
      tension: 0.35,
      pointBackgroundColor: '#348fe2',
      pointBorderColor: '#fff',
      pointHoverRadius: 6,
      pointRadius: 4,
      borderWidth: 2
    }]
  };

  const timelineChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => `Date: ${items[0]?.label || ""}`,
          label: (item) => ` Clicks: ${item.parsed.y}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 }
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
          font: { size: 10 }
        }
      }
    }
  };

  // 2. Devices (Doughnut Chart)
  const deviceDoughnutData = {
    labels: (activeCharts.devices || []).map(d => d.device || 'Unknown'),
    datasets: [{
      data: (activeCharts.devices || []).map(d => d.count),
      backgroundColor: ['#348fe2', '#00acac', '#ff5b57', '#f59c1a', '#8753de', '#b6c2c9'],
      borderWidth: 0,
      hoverOffset: 4
    }]
  };

  const deviceDoughnutOptions = {
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      }
    }
  };

  // 3. Top UTM Sources (Bar Chart)
  const utmSourceBarData = {
    labels: (activeCharts.utmSources || []).map(s => s.source || '(none)'),
    datasets: [{
      label: 'Clicks',
      data: (activeCharts.utmSources || []).map(s => s.count),
      backgroundColor: '#348fe2',
      borderRadius: 4
    }]
  };

  // 4. Top UTM Campaigns (Bar Chart)
  const utmCampaignBarData = {
    labels: (activeCharts.campaigns || []).map(c => c.campaign || '(none)'),
    datasets: [{
      label: 'Clicks',
      data: (activeCharts.campaigns || []).map(c => c.count),
      backgroundColor: '#8753de',
      borderRadius: 4
    }]
  };

  // 5. Top UTM Mediums (Bar Chart)
  const utmMediumBarData = {
    labels: (activeCharts.utmMediums || []).map(m => m.medium || '(none)'),
    datasets: [{
      label: 'Clicks',
      data: (activeCharts.utmMediums || []).map(m => m.count),
      backgroundColor: '#00acac',
      borderRadius: 4
    }]
  };

  // 6. Channels / Pages (Bar Chart)
  const channelBarData = {
    labels: (activeCharts.channels || []).map(c => c.pagename || 'Unknown'),
    datasets: [{
      label: 'Clicks',
      data: (activeCharts.channels || []).map(c => c.count),
      backgroundColor: '#00acac',
      borderRadius: 4
    }]
  };

  const horizontalBarOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label || "",
          label: (item) => ` Clicks: ${Number(item.parsed.x || 0).toLocaleString()}`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          font: { size: 10 },
          maxTicksLimit: 6,
          callback: (val) => {
            if (val >= 1000000) return (val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 1) + 'M';
            if (val >= 1000) return (val / 1000).toFixed(val % 1000 === 0 ? 0 : 1) + 'k';
            return val;
          }
        }
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { size: 10 },
          callback: function(val) {
            const label = this.getLabelForValue(val);
            if (typeof label === 'string' && label.length > 18) {
              return label.substring(0, 16) + '...';
            }
            return label;
          }
        }
      }
    }
  };

  const renderDateRangeInput = (name, isDetail = false) => {
    const formState = isDetail ? detailFormInputs : groupedFormInputs;
    const setFormState = isDetail ? setDetailFormInputs : setGroupedFormInputs;
    const setFilterState = isDetail ? setDetailFilters : setGroupedFilters;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: "135px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
          <span style={{ fontSize: "10px", color: "#666", width: "32px", fontWeight: "600" }}>From:</span>
          <input
            type="date"
            name={`from_${name}`}
            value={formState[`from_${name}`] || ""}
            onChange={(e) => {
              const val = e.target.value;
              setFormState(prev => ({ ...prev, [`from_${name}`]: val }));
              setFilterState(prev => ({ ...prev, [`from_${name}`]: val, page: 1 }));
            }}
            style={{ width: "100%", padding: "2px 4px", fontSize: "11px", borderRadius: "3px", border: "1px solid #ccc" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
          <span style={{ fontSize: "10px", color: "#666", width: "32px", fontWeight: "600" }}>To:</span>
          <input
            type="date"
            name={`to_${name}`}
            value={formState[`to_${name}`] || ""}
            onChange={(e) => {
              const val = e.target.value;
              setFormState(prev => ({ ...prev, [`to_${name}`]: val }));
              setFilterState(prev => ({ ...prev, [`to_${name}`]: val, page: 1 }));
            }}
            style={{ width: "100%", padding: "2px 4px", fontSize: "11px", borderRadius: "3px", border: "1px solid #ccc" }}
          />
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RENDER: DETAIL VIEW ──────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "detail") {
    return (
      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content" className="">
          <div className="">

            {/* Top Detail Navigation Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2" style={{ background: "#fff", padding: "10px 16px", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => setView("grouped")}
                  title="Back to Grouped UTM View"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to UTM Overview
                </button>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#242a30', marginLeft: '8px' }}>
                  {selectedUtm.source || "(none)"} &nbsp;/&nbsp; {selectedUtm.campaign || "(none)"} &nbsp;/&nbsp; {selectedUtm.medium || "(none)"}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>From:</span>
                  <input
                    type="date"
                    value={detailFormInputs.from_created_on || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDetailFormInputs(prev => ({ ...prev, from_created_on: val }));
                      setDetailFilters(prev => ({ ...prev, from_created_on: val, page: 1 }));
                    }}
                    style={{ padding: "3px 6px", fontSize: "12px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>To:</span>
                  <input
                    type="date"
                    value={detailFormInputs.to_created_on || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDetailFormInputs(prev => ({ ...prev, to_created_on: val }));
                      setDetailFilters(prev => ({ ...prev, to_created_on: val, page: 1 }));
                    }}
                    style={{ padding: "3px 6px", fontSize: "12px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </div>

                {hasAnyDetailFilter() && (
                  <button
                    className="btn btn-default btn-xs ml-1"
                    style={{ marginLeft: "4px" }}
                    onClick={clearDetailFilters}
                    title="Clear all filters"
                  >
                    Clear Filter
                  </button>
                )}

                <button className="btn btn-default btn-sm mx-1" onClick={fetchDetailReport} title="Refresh">
                  <FontAwesomeIcon icon={faSync} spin={detailLoading} />
                </button>
                <button className="btn btn-default btn-sm mx-1" onClick={handleExport} disabled={exportLoading} title="Export Detail">
                  <FontAwesomeIcon icon={faDownload} /> {exportLoading ? "Exporting..." : "Export Report"}
                </button>
              </div>
            </div>

            {/* Metric Cards */}
            {detailLoading && !detailData.length ? (
              <div className="row mb-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={`det-skel-${i}`} className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                    <div className="widget widget-stats bg-light inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                      <div className="skeleton-text" style={{ width: "70px", height: "16px" }}></div>
                      <div className="skeleton-text" style={{ width: "90px", height: "32px", alignSelf: "flex-end" }}></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row mb-3">
                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-blue-dark inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Total Clicks
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.total?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-blue inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Desktop
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.desktop?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-green inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Android
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.android?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-red inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      iOS
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.ios?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-purple inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Unique IPs
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.uniqueIps?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-orange inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      UTM Tagged
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {detailMetrics.utmClicks?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Charts: Line Chart for Click Timeline + Doughnut for Devices */}
            <div className="row mb-4">
              <div className="col-lg-7 mb-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading">
                    <h4 className="panel-title">Click Activity Timeline</h4>
                  </div>
                  <div className="panel-body p-4">
                    {detailMetrics.total > 0 ? (
                      <div style={{ height: "260px", position: 'relative' }}>
                        <Line
                          data={timelineChartData}
                          options={timelineChartOptions}
                        />
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4">No click activity data</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-5 mb-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading">
                    <h4 className="panel-title">Device Breakdown</h4>
                  </div>
                  <div className="panel-body p-4">
                    {(detailCharts.devices || []).length > 0 ? (
                      <div style={{ height: "260px", position: 'relative' }}>
                        <Doughnut
                          data={deviceDoughnutData}
                          options={deviceDoughnutOptions}
                        />
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4">No device data</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Detail Channels Breakdown (if available) */}
            {(detailCharts.channels || []).length > 0 && (
              <div className="row mb-4">
                <div className="col-lg-12 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Channels / Pages Breakdown</h4>
                    </div>
                    <div className="panel-body p-4">
                      <div style={{ height: "200px", position: 'relative' }}>
                        <Bar
                          data={channelBarData}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Panel Heading Action Bar */}
            <div className="panel-heading d-flex justify-content-between">
              <div className="col-md-4">
                <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
                  {hasAnyDetailFilter() && (
                    <button
                      className="btn btn-default btn-xs ml-1"
                      onClick={clearDetailFilters}
                      title="Clear all filters"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
              <div>
                <span style={{ fontSize: "0.8rem" }} className="me-3">
                  {detailLoading ? (
                    <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
                  ) : (
                    detailShowingRecords
                  )}
                </span>
                <button
                  className={`btn btn-default btn-sm ${detailFilters.page === 1 ? "d-none" : ""}`}
                  title="Previous"
                  onClick={() => handleDetailPageChange(detailFilters.page - 1)}
                  disabled={detailFilters.page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className={`btn btn-default btn-sm ${detailFilters.page === detailTotalPages ? "d-none" : ""}`}
                  title="Next"
                  onClick={() => handleDetailPageChange(detailFilters.page + 1)}
                  disabled={detailFilters.page === detailTotalPages}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>

            {/* Detail Records Table */}
            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="large-table-container-3">
                <div
                  ref={detailTopScrollbarRef}
                  onScroll={handleDetailTopScroll}
                  style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    height: "16px",
                    marginBottom: "2px",
                    background: "#f8f9fa",
                  }}
                >
                  <div ref={detailTopScrollbarContentRef} style={{ height: "1px" }}></div>
                </div>

                <div
                  ref={detailTableContainerRef}
                  onScroll={syncDetailScroll}
                  style={{
                    overflowY: "auto",
                    height: "calc(112vh - 250px)",
                    overflowX: "auto",
                    position: "relative",
                  }}
                >
                  <table className="table table-condensed table-hover bg-white sticky-table">
                    <thead>
                      <tr style={{ cursor: 'pointer' }}>
                        <th onClick={() => handleDetailSort('id')}>
                          ID <FontAwesomeIcon icon={getDetailSortIcon('id')} />
                        </th>
                        <th onClick={() => handleDetailSort('stype')}>
                          Platform <FontAwesomeIcon icon={getDetailSortIcon('stype')} />
                        </th>
                        <th onClick={() => handleDetailSort('pagename')}>
                          Page / Channel <FontAwesomeIcon icon={getDetailSortIcon('pagename')} />
                        </th>
                        <th onClick={() => handleDetailSort('device_type')}>
                          Device <FontAwesomeIcon icon={getDetailSortIcon('device_type')} />
                        </th>
                        <th onClick={() => handleDetailSort('request_url')}>
                          Request URL <FontAwesomeIcon icon={getDetailSortIcon('request_url')} />
                        </th>
                        <th onClick={() => handleDetailSort('ip_address')}>
                          IP Address <FontAwesomeIcon icon={getDetailSortIcon('ip_address')} />
                        </th>
                        <th onClick={() => handleDetailSort('referer')}>
                          Referer <FontAwesomeIcon icon={getDetailSortIcon('referer')} />
                        </th>
                        <th onClick={() => handleDetailSort('user_agent')}>
                          User Agent <FontAwesomeIcon icon={getDetailSortIcon('user_agent')} />
                        </th>
                        <th onClick={() => handleDetailSort('created_on')}>
                          Created On <FontAwesomeIcon icon={getDetailSortIcon('created_on')} />
                        </th>
                      </tr>

                      <tr>
                        <th><p>&nbsp;</p></th>
                        <th><p>&nbsp;</p></th>
                        <th>
                          <div className="d-flex align-items-center gap-1">
                            <input
                              type="text"
                              name="pagename"
                              placeholder="Page..."
                              value={detailFormInputs.pagename}
                              onChange={handleDetailInputChange}
                              onKeyDown={handleDetailKeyDown}
                              style={{ width: "110px", padding: "5px" }}
                            />
                            <button type="button" className="btn btn-default btn-xs btn-group-xs" onClick={applyDetailFilters}>
                              <FontAwesomeIcon icon={faSearch} />
                            </button>
                          </div>
                        </th>
                        <th>
                          <select
                            name="device_type"
                            value={detailFormInputs.device_type}
                            onChange={(e) => {
                              const value = e.target.value;
                              setDetailFormInputs(prev => ({ ...prev, device_type: value }));
                              setDetailFilters(prev => ({ ...prev, device_type: value, page: 1 }));
                            }}
                            style={{ width: "100px", padding: "5px" }}
                          >
                            <option value="">--select--</option>
                            <option value="Desktop">Desktop</option>
                            <option value="Android">Android</option>
                            <option value="iOS">iOS</option>
                          </select>
                        </th>
                        <th>
                          <input
                            type="text"
                            name="request_url"
                            placeholder="URL..."
                            value={detailFormInputs.request_url}
                            onChange={handleDetailInputChange}
                            onKeyDown={handleDetailKeyDown}
                            style={{ width: "130px", padding: "5px" }}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            name="ip_address"
                            placeholder="IP..."
                            value={detailFormInputs.ip_address}
                            onChange={handleDetailInputChange}
                            onKeyDown={handleDetailKeyDown}
                            style={{ width: "100px", padding: "5px" }}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            name="referer"
                            placeholder="Referer..."
                            value={detailFormInputs.referer}
                            onChange={handleDetailInputChange}
                            onKeyDown={handleDetailKeyDown}
                            style={{ width: "110px", padding: "5px" }}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            name="user_agent"
                            placeholder="User Agent..."
                            value={detailFormInputs.user_agent}
                            onChange={handleDetailInputChange}
                            onKeyDown={handleDetailKeyDown}
                            style={{ width: "120px", padding: "5px" }}
                          />
                        </th>
                        <th>
                          {renderDateRangeInput("created_on", true)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailLoading ? (
                        Array.from({ length: 8 }).map((_, idx) => (
                          <tr key={`det-skel-row-${idx}`} className="skeleton-row">
                            <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "85%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                            <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          </tr>
                        ))
                      ) : detailData.length > 0 ? (
                        detailData.map((row, index) => (
                          <tr key={row.id || index}>
                            <td>{row.id}</td>
                            <td>{row.stype || "-"}</td>
                            <td>{row.pagename || "-"}</td>
                            <td>{row.device_type || "-"}</td>
                            <td style={{ maxWidth: "250px" }}>
                              <div className="d-flex align-items-center gap-1">
                                <span className="text-truncate" style={{ maxWidth: "200px" }} title={row.request_url}>
                                  {row.request_url ? (
                                    <a href={row.request_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                                      {row.request_url}
                                    </a>
                                  ) : "-"}
                                </span>
                                {row.request_url && (
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-link text-muted p-0"
                                    onClick={() => copyToClipboard(row.request_url, row.id)}
                                    title="Copy URL"
                                  >
                                    <FontAwesomeIcon icon={copiedId === row.id ? faCheck : faCopy} className={copiedId === row.id ? "text-success" : ""} />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="ctc">{row.ip_address || "-"}</td>
                            <td style={{ maxWidth: "150px" }}>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: "140px" }} title={row.referer}>
                                {row.referer || "-"}
                              </span>
                            </td>
                            <td style={{ maxWidth: "180px" }}>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: "170px" }} title={row.user_agent}>
                                {row.user_agent || "-"}
                              </span>
                            </td>
                            <td className="ctc" style={{ whiteSpace: "nowrap" }}>{formatDisplayDateTime(row.created_on)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center text-muted py-4">
                            No records found for this UTM combination.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                        <td>Total</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RENDER: MAIN UTM GROUPED VIEW ────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="">

          {/* Top Header: Platform Tabs + Date Filters + Actions */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2" style={{ background: "#fff", padding: "10px 16px", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            {/* Left: Platform Tabs */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span style={{ fontSize: "12px", fontWeight: "600", color: "#555" }}>Platform:</span>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${groupedFilters.stype === 'all' ? 'btn-primary' : 'btn-default'}`}
                  onClick={() => handlePlatformChange('all')}
                >
                  All ({tabCounts.all || 0})
                </button>
                <button
                  type="button"
                  className={`btn ${groupedFilters.stype === 'streetgains' ? 'btn-primary' : 'btn-default'}`}
                  onClick={() => handlePlatformChange('streetgains')}
                >
                  Streetgains ({tabCounts.streetgains || 0})
                </button>
                <button
                  type="button"
                  className={`btn ${groupedFilters.stype === 'folio' ? 'btn-primary' : 'btn-default'}`}
                  onClick={() => handlePlatformChange('folio')}
                >
                  Streetfolio ({tabCounts.folio || 0})
                </button>
              </div>
            </div>

            {/* Right: Date Range (From & To) + Clear Filter + Actions */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>From:</span>
                <input
                  type="date"
                  value={groupedFormInputs.from_created_on || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGroupedFormInputs(prev => ({ ...prev, from_created_on: val }));
                    setGroupedFilters(prev => ({ ...prev, from_created_on: val, page: 1 }));
                  }}
                  style={{ padding: "3px 6px", fontSize: "12px", borderRadius: "4px", border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: "#666" }}>To:</span>
                <input
                  type="date"
                  value={groupedFormInputs.to_created_on || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGroupedFormInputs(prev => ({ ...prev, to_created_on: val }));
                    setGroupedFilters(prev => ({ ...prev, to_created_on: val, page: 1 }));
                  }}
                  style={{ padding: "3px 6px", fontSize: "12px", borderRadius: "4px", border: "1px solid #ccc" }}
                />
              </div>

              {hasAnyGroupedFilter() && (
                <button
                  className="btn btn-default btn-xs ml-1"
                  style={{ marginLeft: "4px" }}
                  onClick={clearGroupedFilters}
                  title="Clear all filters"
                >
                  Clear Filter
                </button>
              )}

              <button className="btn btn-default btn-sm mx-1" onClick={fetchGroupedReport} title="Refresh">
                <FontAwesomeIcon icon={faSync} spin={groupedLoading} />
              </button>
              <button className="btn btn-default btn-sm mx-1" onClick={handleExport} disabled={exportLoading} title="Export Report">
                <FontAwesomeIcon icon={faDownload} /> {exportLoading ? "Exporting..." : "Export Report"}
              </button>
            </div>
          </div>

          {/* Metric Cards */}
          {groupedLoading && !groupedData.length ? (
            <>
              <div className="row mb-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={`stat-skeleton-${i}`} className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                    <div className="widget widget-stats bg-light inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                      <div className="skeleton-text" style={{ width: "70px", height: "16px" }}></div>
                      <div className="skeleton-text" style={{ width: "90px", height: "32px", marginTop: "8px" }}></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="row mb-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={`chart-skeleton-${i}`} className="col-lg-6 mb-4">
                    <div className="panel panel-default with-rounded-corner with-shadow h-100">
                      <div className="panel-heading">
                        <h4 className="panel-title"><div className="skeleton-text" style={{ width: "150px" }}></div></h4>
                      </div>
                      <div className="panel-body p-4 d-flex justify-content-center align-items-center">
                        <div className="skeleton-text" style={{ height: "200px", width: "200px", borderRadius: i === 1 ? "50%" : "8px" }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="row mb-3">
                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-blue-dark inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Total Clicks
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.total?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-blue inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Desktop
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.desktop?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-green inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Android
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.android?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-red inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      iOS
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.ios?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-purple inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      Unique IPs
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.uniqueIps?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
                  <div className="widget widget-stats bg-gradient-orange inverse-mode" style={{ minHeight: "92px", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "12px 14px", borderRadius: "6px", marginBottom: "0" }}>
                    <div className="widget-stats-title" style={{ fontSize: "13px", fontWeight: "600", color: "rgba(255,255,255,0.9)", lineHeight: "1.2", textAlign: "left" }}>
                      UTM Tagged
                    </div>
                    <div className="widget-stats-value" style={{ fontSize: "32px", fontWeight: "bold", lineHeight: "1", textAlign: "right" }}>
                      {overviewMetrics.utmClicks?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Row 1: Click Activity Timeline (Line Chart) & Device Breakdown (Doughnut) */}
              <div className="row mb-4">
                <div className="col-lg-7 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Click Activity Timeline</h4>
                    </div>
                    <div className="panel-body p-4">
                      {overviewMetrics.total > 0 ? (
                        <div style={{ height: "260px", position: 'relative' }}>
                          <Line
                            data={timelineChartData}
                            options={timelineChartOptions}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No click activity data</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Device Breakdown</h4>
                    </div>
                    <div className="panel-body p-4">
                      {(overviewCharts.devices || []).length > 0 ? (
                        <div style={{ height: "260px", position: 'relative' }}>
                          <Doughnut
                            data={deviceDoughnutData}
                            options={deviceDoughnutOptions}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No device data</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Row 2: UTM Related Charts (Top Sources, Top Campaigns, Top Mediums) */}
              <div className="row mb-4">
                <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Top UTM Sources</h4>
                    </div>
                    <div className="panel-body p-4">
                      {(overviewCharts.utmSources || []).length > 0 ? (
                        <div style={{ height: "200px", position: 'relative' }}>
                          <Bar
                            data={utmSourceBarData}
                            options={horizontalBarOptions}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No source data</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Top UTM Campaigns</h4>
                    </div>
                    <div className="panel-body p-4">
                      {(overviewCharts.campaigns || []).length > 0 ? (
                        <div style={{ height: "200px", position: 'relative' }}>
                          <Bar
                            data={utmCampaignBarData}
                            options={horizontalBarOptions}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No campaign data</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4">
                  <div className="panel panel-default with-rounded-corner with-shadow h-100">
                    <div className="panel-heading">
                      <h4 className="panel-title">Top UTM Mediums</h4>
                    </div>
                    <div className="panel-body p-4">
                      {(overviewCharts.utmMediums || []).length > 0 ? (
                        <div style={{ height: "200px", position: 'relative' }}>
                          <Bar
                            data={utmMediumBarData}
                            options={horizontalBarOptions}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-4">No medium data</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Header / Above Table Bar */}
          <div className="panel-heading d-flex justify-content-between align-items-center">
            <div className="col-md-6">
              <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#333" }}>
                UTM Source &amp; Campaign Overview
              </span>
            </div>

            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {groupedLoading ? (
                  <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
                ) : (
                  `${groupedData.length} UTM combination${groupedData.length !== 1 ? 's' : ''}`
                )}
              </span>
            </div>
          </div>

          {/* UTM Grouped Table */}
          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="large-table-container-3">
              <div
                ref={groupedTopScrollbarRef}
                onScroll={handleGroupedTopScroll}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div ref={groupedTopScrollbarContentRef} style={{ height: "1px" }}></div>
              </div>

              <div
                ref={groupedTableContainerRef}
                onScroll={syncGroupedScroll}
                style={{
                  overflowY: "auto",
                  height: "calc(112vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>UTM Source</th>
                      <th>UTM Campaign</th>
                      <th>UTM Medium</th>
                      <th className="ctc">Total Clicks</th>
                      <th className="ctc">Desktop</th>
                      <th className="ctc">Android</th>
                      <th className="ctc">iOS</th>
                      <th className="ctc">Unique IPs</th>
                      <th className="ctc">First Click</th>
                      <th className="ctc">Last Click</th>
                    </tr>

                    <tr>
                      <th><p>&nbsp;</p></th>
                      <th>
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="text"
                            name="utm_source"
                            placeholder="Search source..."
                            value={groupedFormInputs.utm_source}
                            onChange={handleGroupedInputChange}
                            onKeyDown={handleGroupedKeyDown}
                            style={{ width: "110px", padding: "5px" }}
                          />
                          <button type="button" className="btn btn-default btn-xs btn-group-xs" onClick={applyGroupedFilters}>
                            <FontAwesomeIcon icon={faSearch} />
                          </button>
                        </div>
                      </th>
                      <th>
                        <input
                          type="text"
                          name="utm_campaign"
                          placeholder="Search campaign..."
                          value={groupedFormInputs.utm_campaign}
                          onChange={handleGroupedInputChange}
                          onKeyDown={handleGroupedKeyDown}
                          style={{ width: "110px", padding: "5px" }}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          name="utm_medium"
                          placeholder="Search medium..."
                          value={groupedFormInputs.utm_medium}
                          onChange={handleGroupedInputChange}
                          onKeyDown={handleGroupedKeyDown}
                          style={{ width: "110px", padding: "5px" }}
                        />
                      </th>
                      <th><p>&nbsp;</p></th>
                      <th><p>&nbsp;</p></th>
                      <th><p>&nbsp;</p></th>
                      <th><p>&nbsp;</p></th>
                      <th><p>&nbsp;</p></th>
                      <th>{renderDateRangeInput("created_on", false)}</th>
                      <th>{renderDateRangeInput("created_on", false)}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupedLoading ? (
                      Array.from({ length: 8 }).map((_, idx) => (
                        <tr key={`grp-skel-row-${idx}`} className="skeleton-row">
                          <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "50%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "55%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                          <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                        </tr>
                      ))
                    ) : groupedData.length > 0 ? (
                      groupedData.map((row, index) => (
                        <tr
                          key={index}
                          style={{ cursor: "pointer" }}
                          onClick={() => openDetail(row)}
                          title="Click to view full detail for this UTM"
                        >
                          <td className="text-muted">{index + 1}</td>
                          <td>{row.utm_source || "-"}</td>
                          <td>{row.utm_campaign || "-"}</td>
                          <td>{row.utm_medium || "-"}</td>
                          <td className="ctc"><strong>{row.total_clicks?.toLocaleString() || 0}</strong></td>
                          <td className="ctc">{row.desktop?.toLocaleString() || 0}</td>
                          <td className="ctc">{row.android?.toLocaleString() || 0}</td>
                          <td className="ctc">{row.ios?.toLocaleString() || 0}</td>
                          <td className="ctc">{row.unique_ips?.toLocaleString() || 0}</td>
                          <td className="ctc" style={{ whiteSpace: "nowrap", fontSize: "11px" }}>{formatDisplayDate(row.first_click)}</td>
                          <td className="ctc" style={{ whiteSpace: "nowrap", fontSize: "11px" }}>{formatDisplayDate(row.last_click)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-4">
                          No UTM groups found for the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot>
                    <tr style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                      <td>Total</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="ctc">
                        {groupedData.reduce((acc, r) => acc + (Number(r.total_clicks) || 0), 0).toLocaleString()}
                      </td>
                      <td className="ctc">
                        {groupedData.reduce((acc, r) => acc + (Number(r.desktop) || 0), 0).toLocaleString()}
                      </td>
                      <td className="ctc">
                        {groupedData.reduce((acc, r) => acc + (Number(r.android) || 0), 0).toLocaleString()}
                      </td>
                      <td className="ctc">
                        {groupedData.reduce((acc, r) => acc + (Number(r.ios) || 0), 0).toLocaleString()}
                      </td>
                      <td className="ctc">
                        {groupedData.reduce((acc, r) => acc + (Number(r.unique_ips) || 0), 0).toLocaleString()}
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LinkTrackingReport;
